import {
  ApprovalRowDetail,
  ApprovalRowFieldAvailability,
} from '@/hooks/useApprovalRowDetails'
import { ApprovalHistoryEntry, ApprovalStatePolicy } from '@/types/approvals'

export type ApprovalScreen = 'xs' | 'sm' | 'md'

const ROOM: Record<ApprovalScreen, number> = { xs: 0, sm: 1, md: 2 }

/** One row of an Approvals table, whichever list it came from. */
export type ApprovalTableRow = {
  id: string
  documentId: string
  /** Null when the policy has come off the document since the action was taken. */
  policy: ApprovalStatePolicy | null
  /** Already-formatted, e.g. "5 days" or "2 days ago". */
  time: string
  detail?: ApprovalRowDetail
  /** Set on a history row: the action that row itself reports. */
  action?: ApprovalHistoryEntry['action']
}

export type ApprovalColumn = {
  key: string
  label: string
  /**
   * In px. The table lays columns out as flex items, so this is the floor
   * and the grow weight at once: a 150 column never gets narrower than 150
   * and takes half again as much spare room as a 100 column.
   */
  size: number
  /** False for a column that must stay exactly `size` wide. */
  grow?: boolean
  align?: 'right'
  /** Narrowest screen this column still appears on. Absent means every screen. */
  needs?: ApprovalScreen
}

/**
 * Every column this project can show, given which fields it has configured.
 * Screen size then narrows these down — see `visibleColumns`.
 */
export function availableColumns(
  availability: ApprovalRowFieldAvailability,
  timeLabel: string,
  /** In px. */
  reviewersWidth = 0,
  showDecision = false,
): ApprovalColumn[] {
  return [
    {
      key: 'name',
      label: availability.vendor ? 'Vendor' : 'Document',
      size: 130,
    },
    ...(availability.amount
      ? [
          {
            key: 'amount',
            label: 'Amount',
            size: 96,
            align: 'right' as const,
            needs: 'sm' as const,
          },
        ]
      : []),
    { key: 'policy', label: 'Policy', size: 100, needs: 'sm' },
    { key: 'state', label: 'Workflow State', size: 140, needs: 'sm' },
    ...(showDecision ? [{ key: 'decision', label: 'Decision', size: 96 }] : []),
    { key: 'time', label: timeLabel, size: 76 },
    { key: 'progress', label: 'Progress', size: 90, needs: 'md' },
    {
      key: 'reviewers',
      label: 'Reviewers',
      size: reviewersWidth,
      grow: false,
      needs: 'md',
    },
  ]
}

export function visibleColumns(
  columns: ApprovalColumn[],
  screen: ApprovalScreen,
): ApprovalColumn[] {
  return columns.filter(({ needs }) => !needs || ROOM[screen] >= ROOM[needs])
}

export function columnVisibility(
  columns: ApprovalColumn[],
  screen: ApprovalScreen,
): Record<string, boolean> {
  const shown = new Set(visibleColumns(columns, screen).map(({ key }) => key))
  return Object.fromEntries(columns.map(({ key }) => [key, shown.has(key)]))
}
